import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { SessionService } from '../session.service.js';
import { HttpParams } from "@angular/common/http";
import { PageEvent } from '@angular/material/paginator';
import { SortEvent } from 'allai-admin-shared';
import { DateUtils } from '@allaihealth/util';
import { DateRange } from '@angular/material/datepicker';

/** Service that manages the appointments list filters to maintain their changes and reloading */
@Injectable({
	providedIn: 'root'
})
export class OrganizationsFilterService {

	/** Pager for list views */
	private _pageEvent: PageEvent;

	/** Global Search */
	private _globalSearch: string;

	/** Global Search */
	private _sortOrder: SortEvent;


	private _metricsStart: Date;
	private _metricsEnd: Date;

	private _userMetricsStart: Date;
	private _userMetricsEnd: Date;


	/** Subject for filter changes */
	private onNewFilterSubject: Subject<void> = new Subject<void>();
	/** Subject for filter changes */
	private onNewDetailFilterSubject: Subject<void> = new Subject<void>();

	constructor(private sessionService: SessionService) {
		//clear on login
		this.sessionService.onLogin.subscribe(() => this.clear());
	}

	/** Dispatch an event on a change */
	private setChanged(savePage?: boolean) {
		if(!savePage) {
			this.pageEvent = null;
		}
		this.onNewFilterSubject.next();
	}

	private setDetailChanged() {
		this.onNewDetailFilterSubject.next();
	}

	/** Clear all saved filters and {@link localStorage} */
	clear() {
	}

	set pageEvent(pageEvent: PageEvent) {
		this._pageEvent = pageEvent;
		this.setChanged(true);
	}

	get pageEvent(): PageEvent {
		if (!this._pageEvent) {
			this._pageEvent = {
				pageIndex: 0,
				pageSize: 10,
				length: 100
			}
		}
		return this._pageEvent;
	}

	set globalSearch(globalSearch: string) {
		this._globalSearch = globalSearch;
		this._pageEvent = null;
		this.setChanged();
	}

	get globalSearch(): string {
		if (!this._globalSearch) {
			this._globalSearch = '';
		}
		return this._globalSearch;
	}

	set sortOrder(sortOrder: SortEvent) {
		this._sortOrder = sortOrder;
		this.setChanged();
	}

	get sortOrder(): SortEvent {
		if (!this._sortOrder) {
			this._sortOrder = {
				active: '',
				direction: ''
			};
		}
		return this._sortOrder;
	}

	/** Dispatched for filter changes */
	get onNewFilter() {
		return this.onNewFilterSubject.asObservable();
	}

	/** Dispatched for filter changes */
	get onNewDetailFilter() {
		return this.onNewDetailFilterSubject.asObservable();
	}

	/** Get the query parameters for a request to the backend server */
	get params(): HttpParams {
		let params: HttpParams = new HttpParams();

		const pageEvent = this.pageEvent;
		const sort: SortEvent = this.sortOrder;

		params = params.set("pageIndex", pageEvent.pageIndex);
		params = params.set("pageSize", pageEvent.pageSize);
		params = params.set("globalSearch", this.globalSearch);
		params = params.set("orderBy", sort.active);
		params = params.set("direction", sort.direction);

		return params;
	}

	get detailParams() {
		let params: HttpParams = new HttpParams();

		const zone = this.sessionService.organization.time_zone;
		const metricsStart = DateUtils.setZonedStartEndDate(this.metricsStartDate, true, zone).toMillis();
		const metricsEnd = DateUtils.setZonedStartEndDate(this.metricsEndDate, true, zone).toMillis();
		const userMetricsStart = DateUtils.setZonedStartEndDate(this.userMetricsStartDate, true, zone).toMillis();
		const userMetricsEnd = DateUtils.setZonedStartEndDate(this.userMetricsEndDate, true, zone).toMillis();

		params = params.set("metricsStart",metricsStart);
		params = params.set("metricsEnd",metricsEnd);
		params = params.set("userMetricsStart",userMetricsStart);
		params = params.set("userMetricsEnd",userMetricsEnd);

		return params;
	}

	set metricsRange(dateRange: DateRange<Date>) {
		if(dateRange.start && dateRange.end) {
			this._metricsStart = dateRange.start;
			this._metricsEnd = dateRange.end;
			this.setDetailChanged();
		}
	}
	set userMetricsRange(dateRange: DateRange<Date>) {
		if(dateRange.start && dateRange.end) {
			this._userMetricsStart = dateRange.start;
			this._userMetricsEnd = dateRange.end;
			this.setDetailChanged();
		}
	}

	get metricsStartDate(): Date {
		if (!this._metricsStart) {
			this._metricsStart = new Date();
			this._metricsStart.setDate(this._metricsStart.getDate() - 360);
		}
		return this._metricsStart;
	}

	get metricsEndDate(): Date {
		if (!this._metricsEnd) {
			this._metricsEnd = new Date();
		}
		return this._metricsEnd;
	}

	get userMetricsStartDate(): Date {
		if (!this._userMetricsStart) {
			this._userMetricsStart = new Date();
			this._userMetricsStart.setDate(this._userMetricsStart.getDate() - 180);
		}
		return this._userMetricsStart;
	}

	get userMetricsEndDate(): Date {
		if (!this._userMetricsEnd) {
			this._userMetricsEnd = new Date();
		}
		return this._userMetricsEnd;
	}
}
